import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabaseApi } from '~/supabase/api'
import { Action } from '~/supabase/types'
import { GET_SHOPIFY_PRODUCTS_QUERY_KEY } from '~/features/shopify/hooks/useGetShopifyProducts'
import { useNotifyError } from '~/features/hooks/useNotifyError'
import { toast } from 'sonner'
import { GET_INVENTORY_QUERY_KEY } from '~/features/hooks/useGetInventory'
import { GET_ACTIONS_QUERY_KEY } from './useGetActions'

export type CreateActionsPayload = Omit<Action, 'id' | 'created_at'>

export const useCreateActions = () => {
  const { mutate: notifyError } = useNotifyError()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['create-actions'],
    mutationFn: async (payload: Array<CreateActionsPayload>) => {
      const promises = await Promise.all(
        payload.map((action) => supabaseApi.from('actions').insert(action).select().single())
      )

      const errors = promises.map((p) => p.error).filter((error) => error !== null)

      if (errors.length > 0) {
        toast.error('Action creation failed')
        notifyError({
          message: 'Action creation failed.',
          data: { payload, errorCount: errors.length },
          error: errors[0],
          function: 'useCreateAction',
        })
        throw new Error('Action creation failed')
      }

      return promises.map((p) => p.data)
    },
    onSuccess: () => {
      toast.success('Action created successfully.')

      // Invalidate both models and shopify products queries
      queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
      // Invalidate shopify products query immediately
      queryClient.invalidateQueries({ queryKey: [GET_SHOPIFY_PRODUCTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_ACTIONS_QUERY_KEY] })
    },
  })
}
